import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import styled from 'styled-components';
import { Redirect, Link } from 'react-router-dom';
import {
  ButtonPrimary,
  ButtonTertiary,
  DialogFullScreen,
} from '@spotify-internal/encore-creator-web';
import {
  selectAggregators,
  LOADING,
  IDLE,
  UNAUTHORIZED,
  CONFLICT,
  ERROR,
} from '../../../store/selectors.ts';
import { loadAggregators } from '../../../store/aggregators.ts';
import { optimisticUpdatePodcastMetadata } from '../../../store/podcastMetadata.ts';
import { PodcastUpdateUrl } from './PodcastUpdateUrl';
import { PodcastUpdateLicensor } from './PodcastUpdateLicensor';
import { PodcastUpdateReview } from './PodcastUpdateReview';
import { trackEvent } from '../../../lib/Tracking/index.ts';
import { Request, poll, toJSON, PODCASTERS_SERVICE } from '../../../lib/Request/index.ts';
import { FormattedMessage, defineMessage, injectIntl } from 'react-intl';

const STEPS = {
  RSS_VERIFICATION: 1,
  UPDATE_LICENSOR: 2,
  REVIEW_AND_SUBMIT: 3,
};

const DuplicateRSSErrorDetected = (
  <React.Fragment>
    Unable to update your podcast's RSS feed. Please{' '}
    <Link to="/faq#how-can-i-contact-the-spotify-for-podcasters-team" target="_blank">
      contact our support team
    </Link>
    .
  </React.Fragment>
);

export class PodcastUpdateModal extends Component {
  static propTypes = {
    onClose: PropTypes.func.isRequired,
    loadAggregators: PropTypes.func.isRequired,
    aggregators: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        passthroughCapable: PropTypes.bool,
      }),
    ),
    existingFeedUrl: PropTypes.string.isRequired,
    showId: PropTypes.string.isRequired,
    hasNetworkPermissions: PropTypes.bool.isRequired,
    intl: PropTypes.shape(),
  };

  state = {
    currentStep: STEPS.RSS_VERIFICATION,
    feedUrlFromBackend: '',
    newFeedUrl: '',
    licensor: '',
    isFeedPassthroughCapable: false,
    feedUrlErrors: undefined,
    status: IDLE,
    networkId: '',
    isNextButtonDisabled: true,
    duplicateRSSError: false,
  };

  componentDidUpdate(_, prevState) {
    if (
      prevState.currentStep === STEPS.RSS_VERIFICATION &&
      this.state.currentStep === STEPS.UPDATE_LICENSOR
    ) {
      if (this.hasPassthroughMismatch(this.state.isFeedPassthroughCapable, this.state.networkId)) {
        this.setState({
          status: ERROR,
          isNextButtonDisabled: true,
        });

        trackEvent({
          category: 'feed-update',
          action: 'licensor-passthough-error',
          label: this.state.networkId,
        });
      }
    }
  }

  validateFeed = async newFeedUrl => {
    this.setState({ status: LOADING, isNextButtonDisabled: true });
    try {
      const { token } = await Request.of()
        .post(`${PODCASTERS_SERVICE}/v0/feeds/evaluate`, {
          feedUrl: newFeedUrl,
          showId: this.props.showId,
        })
        .then(toJSON);

      const data = await poll(() =>
        Request.of()
          .get(`${PODCASTERS_SERVICE}/v0/feeds/evaluate/${token}`)
          .then(toJSON),
      );

      /* istanbul ignore next */
      const errorsAllowingExistingShows = data.errors || [];
      this.logFeedValidationResult(errorsAllowingExistingShows);
      this.setState({
        feedUrlFromBackend: data.feedUrl,
        feedUrlErrors: errorsAllowingExistingShows,
        status: IDLE,
        networkId: data.network.id,
        isNextButtonDisabled: Boolean(errorsAllowingExistingShows.length),
        isFeedPassthroughCapable: data.passthroughCapable,
      });
    } catch (e) {
      this.setState({
        feedUrlErrors: ['UNKNOWN_ERROR'],
        status: IDLE,
      });
      trackEvent({
        category: 'feed-update',
        action: 'feed-url',
        label: 'SERVER_ERROR',
      });
    }
  };

  componentDidMount() {
    this.props.loadAggregators();
  }

  logFeedValidationResult = errors => {
    if (Boolean(errors.length)) {
      trackEvent({
        category: 'feed-update',
        action: 'feed-url-invalid',
        label: errors.toString(),
      });
    } else {
      trackEvent({
        category: 'feed-update',
        action: 'feed-url',
        label: 'valid',
      });
    }
  };

  aggregators = () => {
    const defaultAggregator = defineMessage({
      id: 'podcast-update.default=aggregator',
      defaultMessage: "Other / I don't know",
    });
    /* istanbul ignore else */
    if (this.props.aggregators) {
      return this.props.aggregators.map(row => {
        const newRow = { ...row };
        /* istanbul ignore next */
        if (!row.name) newRow.name = this.props.intl.formatMessage(defaultAggregator);
        return newRow;
      });
    }

    /* istanbul ignore next */
    return [];
  };

  newAggregator = () => {
    return this.aggregators().find(elem => elem.id === this.state.networkId);
  };

  cancelButton = () => {
    return (
      <ButtonTertiary key="close" onClick={this.props.onClose}>
        <FormattedMessage id="podcast-update.cancel" defaultMessage="Cancel" />
      </ButtonTertiary>
    );
  };

  previousButton = () => {
    return (
      <ButtonTertiary key="previous" data-qa="previous-button" onClick={this.handlePreviousClick}>
        <FormattedMessage id="podcast-update.go-back" defaultMessage="Go Back" />
      </ButtonTertiary>
    );
  };

  nextButton = () => {
    return (
      <ButtonPrimary
        key="next"
        data-qa="next-button"
        data-testid="next-button"
        onClick={async () => {
          this.handleNextClick();
        }}
        disabled={this.state.isNextButtonDisabled}
      >
        <FormattedMessage id="podcast-update.next" defaultMessage="Next" />
      </ButtonPrimary>
    );
  };

  submitButton = () => {
    return (
      <ButtonPrimary
        data-testid="submit-button"
        key="submit"
        onClick={this.handleSubmitClick}
        disabled={
          this.props.submissionStatus === CONFLICT || this.props.submissionStatus === LOADING
        }
      >
        {/* istanbul ignore next */ this.props.submissionStatus === LOADING ? (
          <FormattedMessage id="podcast-update.submitting" defaultMessage="Submitting..." />
        ) : (
          <FormattedMessage id="podcast-update.submit" defaultMessage="Submit" />
        )}
      </ButtonPrimary>
    );
  };

  hasPassthroughMismatch = (isFeedPassthroughCapable, aggregatorId) => {
    // if the feed isn't passthough capable but the new hosting provider is,
    // then the podcast will dissappear from Spotify since we cant' ingest it.
    const selectedAggregator = this.aggregators().find(elem => elem.id === aggregatorId);
    return !isFeedPassthroughCapable && selectedAggregator.passthroughCapable;
  };

  handleFeedUrlChange = /* istanbul ignore next */ ({ target: { value } }) => {
    this.setState({ newFeedUrl: value });
  };

  handleLicensorChange = /* istanbul ignore next */ ({ target: { value } }) => {
    this.setState({
      status: IDLE,
      isNextButtonDisabled: false,
    });

    if (this.hasPassthroughMismatch(this.state.isFeedPassthroughCapable, value)) {
      this.setState({
        status: ERROR,
        isNextButtonDisabled: true,
      });

      trackEvent({
        category: 'feed-update',
        action: 'licensor-passthough-error',
        label: value,
      });
      return;
    }
    trackEvent({
      category: 'feed-update',
      action: 'licensor-changed',
      label: value,
    });
    this.setState({ networkId: value });
  };

  handlePreviousClick = () => {
    this.setState({
      status: IDLE,
      isNextButtonDisabled: false,
      duplicateRSSError: false,
    });
    // Network users should not be able to update the network a podcast is on
    if (this.state.currentStep === STEPS.REVIEW_AND_SUBMIT && this.props.hasNetworkPermissions) {
      this.setState({
        currentStep: STEPS.RSS_VERIFICATION,
      });
    } else {
      this.setState(state => ({
        currentStep: state.currentStep - 1,
      }));
    }
  };

  handleNextClick = () => {
    // Network users should not be able to update the network a podcast is on
    if (this.state.currentStep === STEPS.RSS_VERIFICATION && this.props.hasNetworkPermissions) {
      this.setState({ currentStep: STEPS.REVIEW_AND_SUBMIT });
    } else {
      this.setState(state => ({
        currentStep: state.currentStep + 1,
      }));
    }
  };

  handleSubmitClick = () => {
    const { undoChanges } = this.props.optimisticUpdatePodcastMetadata({
      feedUrl: this.state.feedUrlFromBackend,
      network: this.newAggregator(),
    });

    return Request.of()
      .put(`${PODCASTERS_SERVICE}/v0/shows/${this.props.showId}/metadata`, {
        feedUrl: this.state.feedUrlFromBackend,
        network: {
          id: this.state.networkId,
        },
      })
      .then(() => {
        trackEvent({
          category: 'feed-update',
          action: 'update',
          label: 'success',
        });
        this.props.onClose();
      })
      .catch(error => {
        undoChanges();

        this.setState({
          status: ERROR,
          duplicateRSSError: Boolean(error.response && error.response.status === 409),
        });

        trackEvent({
          category: 'feed-update',
          action: 'update',
          label: 'failed',
        });
      });
  };

  renderModalBody = currentStep => {
    let Panel;
    switch (currentStep) {
      case STEPS.RSS_VERIFICATION: {
        Panel = (
          <PodcastUpdateUrl
            existingFeedUrl={this.props.existingFeedUrl}
            onChange={this.handleFeedUrlChange}
            podcastRssLink={this.state.newFeedUrl}
            validateFeed={this.validateFeed}
            errors={this.state.feedUrlErrors}
            status={this.state.status}
          />
        );
        break;
      }
      case STEPS.UPDATE_LICENSOR: {
        Panel = (
          <PodcastUpdateLicensor
            aggregators={this.aggregators()}
            suggestedNetworkId={this.state.networkId}
            onChange={this.handleLicensorChange}
            status={this.state.status}
            intl={this.props.intl}
          />
        );
        break;
      }
      case STEPS.REVIEW_AND_SUBMIT: {
        Panel = (
          <PodcastUpdateReview
            newFeedUrl={this.state.newFeedUrl}
            aggregator={this.newAggregator()}
            shouldShowAggregators={!this.props.hasNetworkPermissions}
            status={this.state.status}
            errorMessage={
              this.state.status === ERROR && this.state.duplicateRSSError
                ? DuplicateRSSErrorDetected
                : undefined
            }
          />
        );
        break;
      }
      /* istanbul ignore next */
      default: {
        Panel = null;
        break;
      }
    }
    return <Fragment key="panel">{Panel}</Fragment>;
  };

  renderModalFooter = currentStep => {
    const FooterDiv = styled.div`
      height: 48px;
    `;
    var footerButtons = [];

    switch (currentStep) {
      case STEPS.RSS_VERIFICATION: {
        footerButtons = [this.cancelButton(), this.nextButton()];
        break;
      }
      case STEPS.UPDATE_LICENSOR: {
        footerButtons = [this.previousButton(), this.nextButton()];
        break;
      }
      case STEPS.REVIEW_AND_SUBMIT: {
        footerButtons = [this.previousButton(), this.submitButton()];
        break;
      }
      /* istanbul ignore next */
      default: {
        break;
      }
    }
    return <FooterDiv>{footerButtons}</FooterDiv>;
  };

  render() {
    /* istanbul ignore next */
    if (
      this.props.validationStatus === UNAUTHORIZED ||
      this.props.submissionStatus === UNAUTHORIZED
    ) {
      return <Redirect to="/no-access" />;
    }
    const { currentStep } = this.state;
    return (
      <DialogFullScreen
        data-testid="update-rss-overlay"
        onClose={this.props.onClose}
        dialogTitle="&nbsp;"
        body={this.renderModalBody(currentStep)}
        bodyStyle={{ padding: '0' }}
        footer={this.renderModalFooter(currentStep)}
      />
    );
  }
}

const selectors = createStructuredSelector({
  aggregators: selectAggregators,
});

const actions = {
  loadAggregators,
  optimisticUpdatePodcastMetadata,
};

export const ConnectedPodcastUpdateModal = connect(
  selectors,
  actions,
)(injectIntl(PodcastUpdateModal));
